import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Target,
  Eye,
  Heart,
  Shield,
  Clock,
  Handshake,
  Users,
  Award
} from 'lucide-react';

export default function AboutPage() {
  const values = [
    {
      title: 'Reliability',
      description: 'We deliver on our promises, every time. Your staffing needs are our priority.',
      icon: Shield
    },
    {
      title: 'Speed',
      description: 'Fast turnaround without compromising quality. We understand urgency.',
      icon: Clock
    },
    {
      title: 'Compliance',
      description: 'Full adherence to Ontario labour laws and workplace safety standards.',
      icon: Award
    },
    {
      title: 'Partnership',
      description: 'We build lasting relationships, not just transactions.',
      icon: Handshake
    }
  ];

  const stats = [
    { number: '15+', label: 'Years in Business' },
    { number: '500+', label: 'Monthly Placements' },
    { number: '200+', label: 'Client Partners' },
    { number: '98%', label: 'Satisfaction Rate' }
  ];

  return (
    <main data-testid="about-page">
      {/* Hero Section */}
      <section className="page-header" data-testid="about-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium mb-6">
              About FleetHire
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Your Trusted Staffing Partner
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              For over 15 years, FleetHire Staffing Solutions has been connecting businesses with reliable talent across the Greater Toronto Area.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]" data-testid="about-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="section-padding bg-white" data-testid="company-overview">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                Our Story
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight mb-6">
                Building Workforces, Building Futures
              </h2>
              <div className="space-y-4 text-[#64748B] leading-relaxed">
                <p>
                  Founded in Mississauga in 2010, FleetHire Staffing Solutions started with a simple mission: to simplify hiring for businesses while creating opportunities for workers in our community.
                </p>
                <p>
                  What began as a small team serving local warehouses has grown into a full-service staffing agency, placing hundreds of workers every month across the Greater Toronto Area.
                </p>
                <p>
                  Our success is built on relationships—with the businesses we serve and the workers we place. We take pride in understanding the unique needs of each client and matching them with candidates who don't just fill a role, but add value to their operations.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div 
                className="aspect-[4/3] rounded-lg bg-cover bg-center"
                style={{
                  backgroundImage: 'url(https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg)'
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="mission-vision">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white border border-[#E2E8F0] p-8 md:p-12"
            >
              <div className="w-14 h-14 bg-[#0B1F3A] rounded-lg flex items-center justify-center mb-6">
                <Target size={28} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-['Chivo'] font-black text-2xl text-[#0B1F3A] mb-4">
                Our Mission
              </h3>
              <p className="text-[#64748B] leading-relaxed text-lg">
                To simplify hiring and connect businesses with reliable talent, creating value for employers and meaningful opportunities for workers across the GTA.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-[#E2E8F0] p-8 md:p-12"
            >
              <div className="w-14 h-14 bg-[#1E90FF] rounded-lg flex items-center justify-center mb-6">
                <Eye size={28} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="font-['Chivo'] font-black text-2xl text-[#0B1F3A] mb-4">
                Our Vision
              </h3>
              <p className="text-[#64748B] leading-relaxed text-lg">
                To be the most trusted staffing partner in the Greater Toronto Area, known for speed, reliability, and the quality of our placements.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white" data-testid="values-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              What Drives Us
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="value-card"
              >
                <div className="value-card-icon">
                  <value.icon size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  {value.title}
                </h3>
                <p className="text-[#64748B] text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="team-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-sm text-[#94A3B8] uppercase tracking-[0.2em] font-bold">
                Our Team
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-white mt-3 tracking-tight mb-6">
                Dedicated Professionals
              </h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                Our team of experienced recruiters and account managers brings decades of combined experience in the staffing industry. We understand the GTA market, and we're committed to delivering results.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex -space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#1E90FF] flex items-center justify-center text-white font-bold border-2 border-[#0B1F3A]">JD</div>
                  <div className="w-12 h-12 rounded-full bg-[#FF6B00] flex items-center justify-center text-white font-bold border-2 border-[#0B1F3A]">MK</div>
                  <div className="w-12 h-12 rounded-full bg-[#1E90FF] flex items-center justify-center text-white font-bold border-2 border-[#0B1F3A]">SR</div>
                  <div className="w-12 h-12 rounded-full bg-[#FF6B00] flex items-center justify-center text-white font-bold border-2 border-[#0B1F3A]">LP</div>
                </div>
                <span className="text-[#94A3B8]">+ 15 more team members</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <Users size={32} className="text-[#FF6B00] mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-['Chivo'] font-black text-2xl text-white">20+</div>
                <div className="text-[#94A3B8] text-sm">Team Members</div>
              </div>
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <Award size={32} className="text-[#FF6B00] mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-['Chivo'] font-black text-2xl text-white">50+</div>
                <div className="text-[#94A3B8] text-sm">Years Combined Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] tracking-tight mb-4">
              Ready to Work With Us?
            </h2>
            <p className="text-[#64748B] text-lg max-w-2xl mx-auto mb-8">
              Whether you're looking to hire or looking for work, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/employers"
                className="btn-primary btn-hover-arrow text-lg px-8 py-4"
                data-testid="about-employers-cta"
              >
                I'm Hiring
                <ArrowRight size={20} className="arrow-icon" strokeWidth={2} />
              </Link>
              <Link
                to="/job-seekers"
                className="btn-secondary text-lg px-8 py-4"
                data-testid="about-jobs-cta"
              >
                I'm Looking for Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
